import type { MenuRoute } from '../config/menu';
import { UserRole, UserRoleValues } from '@talent-week/shared-types';

/**
 * 权限工具函数
 *
 * 提供基于角色的权限检查、菜单过滤等功能。
 */

/**
 * 权限定义
 *
 * 定义每个角色拥有的权限列表。
 * SUPER_ADMIN 拥有所有权限。
 */
export const PERMISSION_MAP: Record<string, string[]> = {
  [UserRole.SUPER_ADMIN]: ['*'],
  [UserRole.EVENT_ADMIN]: [
    'event:view',
    'event:edit',
    'guest:view',
    'guest:edit',
    'guest:import',
    'checkin:view',
    'checkin:manage',
    'notification:view',
    'notification:send',
    'dashboard:view',
  ],
  [UserRole.LEADER_READONLY]: [
    'event:view',
    'guest:view',
    'checkin:view',
    'notification:view',
    'dashboard:view',
  ],
};

/**
 * 检查角色是否拥有指定权限
 *
 * @param role - 用户角色
 * @param permission - 权限标识
 * @returns 是否拥有权限
 */
export function hasPermission(
  role: string | null | undefined,
  permission: string,
): boolean {
  if (!role) return false;

  const permissions = PERMISSION_MAP[role];
  if (!permissions) return false;

  // 超级管理员拥有所有权限
  if (permissions.includes('*')) return true;

  return permissions.includes(permission);
}

/**
 * 检查角色是否拥有任意一个权限
 *
 * @param role - 用户角色
 * @param permissions - 权限标识列表
 * @returns 是否拥有任意一个权限
 */
export function hasAnyPermission(
  role: string | null | undefined,
  permissions: string[],
): boolean {
  return permissions.some((p) => hasPermission(role, p));
}

/**
 * 检查角色是否拥有全部权限
 *
 * @param role - 用户角色
 * @param permissions - 权限标识列表
 * @returns 是否拥有全部权限
 */
export function hasAllPermissions(
  role: string | null | undefined,
  permissions: string[],
): boolean {
  return permissions.every((p) => hasPermission(role, p));
}

/**
 * 检查角色是否可以访问指定路由
 *
 * @param role - 用户角色
 * @param requiredRoles - 路由要求的角色列表（为空表示所有角色可访问）
 * @returns 是否可以访问
 */
export function canAccessRoute(
  role: string | null | undefined,
  requiredRoles?: string[],
): boolean {
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }

  if (!role) return false;

  // 超级管理员可以访问所有路由
  if (role === UserRole.SUPER_ADMIN) return true;

  return requiredRoles.includes(role);
}

/**
 * 根据角色过滤菜单
 *
 * @param menus - 菜单配置
 * @param role - 用户角色
 * @returns 过滤后的菜单
 */
export function filterMenusByRole(
  menus: MenuRoute[],
  role: string | null | undefined,
): MenuRoute[] {
  if (!role) return [];

  // 超级管理员可以看到所有菜单
  if (role === UserRole.SUPER_ADMIN) {
    return menus.filter((m) => !m.hideInMenu);
  }

  return menus
    .filter((menu) => {
      if (menu.hideInMenu) return false;
      if (!menu.roles || menu.roles.length === 0) return true;
      return menu.roles.includes(role);
    })
    .map((menu) => {
      if (menu.children) {
        return {
          ...menu,
          children: filterMenusByRole(menu.children, role),
        };
      }
      return menu;
    })
    .filter((menu) => {
      // 如果有子菜单但过滤后为空，则隐藏
      if (menu.children && menu.children.length === 0) return false;
      return true;
    });
}

/**
 * 获取角色中文名称
 *
 * @param role - 角色标识
 * @returns 角色中文名称
 */
export function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    [UserRole.SUPER_ADMIN]: '超级管理员',
    [UserRole.EVENT_ADMIN]: '活动管理员',
    [UserRole.LEADER_READONLY]: '领导只读',
  };
  return labels[role] || '未知角色';
}

/**
 * 获取所有有效角色值
 */
export function getValidRoles(): string[] {
  return UserRoleValues;
}
